import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const u = await login(form.email, form.password, remember);
        toast.success("Welcome back.");
        navigate(u?.role === "admin" ? "/admin" : "/dashboard");
      } else {
        const res = await register(form.name, form.email, form.password);
        setPendingEmail(res?.email || form.email);
        toast.success("Check your inbox to verify your email.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (!pendingEmail) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: pendingEmail, origin_url: window.location.origin });
      toast.success("Verification email re-sent.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setResending(false); }
  };

  const fld = "bg-secondary border-white/10 rounded-none h-12";

  if (pendingEmail) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          data-testid="verification-sent-view"
          className="w-full max-w-md border border-white/10 bg-[#121212] p-10 text-center">
          <Link to="/" className="block mb-8">
            <span className="font-heading text-3xl text-primary">7 Star</span>
            <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground">Limo Services</div>
          </Link>
          <Mail className="text-primary mx-auto mb-4" size={40} />
          <h1 className="text-2xl text-foreground mb-2">Verify your email</h1>
          <p className="text-sm text-muted-foreground mb-6">
            We sent a verification link to{" "}
            <span className="text-foreground" data-testid="verification-email">{pendingEmail}</span>.
            Click the link in the email to activate your account and sign in.
          </p>
          <Button onClick={resend} disabled={resending} variant="outline" data-testid="resend-verification"
            className="w-full border-white/20 rounded-none uppercase tracking-wide h-11">
            <RefreshCw size={15} className="mr-2" /> {resending ? "Sending..." : "Resend email"}
          </Button>
          <p className="text-xs text-muted-foreground mt-5">
            Already verified?{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="verified-signin">Sign in</Link>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md border border-white/10 bg-[#121212] p-10">
        <Link to="/" className="block text-center mb-8">
          <span className="font-heading text-3xl text-primary">7 Star</span>
          <div className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground">Limo Services</div>
        </Link>
        <h1 className="text-2xl text-foreground mb-1">{isLogin ? "Sign In" : "Create Account"}</h1>
        <p className="text-sm text-muted-foreground mb-8">{isLogin ? "Access your reservations." : "Manage bookings from one place."}</p>

        <form onSubmit={submit} data-testid="auth-form" className="space-y-5">
          {!isLogin && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full Name</Label>
              <Input required data-testid="auth-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fld} />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
            <Input required type="email" data-testid="auth-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fld} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
            <Input required type="password" data-testid="auth-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={fld} />
          </div>

          {isLogin && (
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-wrap">
              <Checkbox id="remember-me" data-testid="remember-me" checked={remember} onCheckedChange={(c) => setRemember(!!c)} />
              <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Keep me signed in</span>
            </label>
          )}

          <Button type="submit" disabled={loading} data-testid="auth-submit"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wide h-12">
            {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
          </Button>
        </form>

        <p className="text-sm text-muted-foreground text-center mt-6">
          {isLogin ? "New to 7 Star? " : "Already have an account? "}
          <Link to={isLogin ? "/register" : "/login"} className="text-primary hover:underline" data-testid="auth-switch">
            {isLogin ? "Create an account" : "Sign in"}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
